import config from '../config';
import { PaginatedData } from './util';
import {
    ProductDef,
    InventoryDef,
    PlayStoreSubscription,
    AppStoreSubscription,
    SubscriptionDef,
} from './inventory';
import { XsollaWebhookSubscription } from './xsolla';
import { StripeSubscription } from './stripe';
import { SteamSubscription } from './steam';

const LogPrefix = 'analytics';

export const MIN_STREAMER_STANDING_DONATION = 2000;
export const MIN_STREAMER_STANDING_SESSIONS_MS = 72000000;

export class StreamerStanding {
    user_id: string = '';
    donation_received_last_30_days: number = 0;
    total_session_ms_last_30_days: number = 0;
}

export interface StreamerStandingResponse {
    type: string;
    data: StreamerStanding[];
    page: PaginatedData;
}

export const isGoodStanding = async (token: string, user_id: string): Promise<boolean> => {
    return getStreamerStanding(user_id, '', token).then((resp) => {
        return (
            resp != null &&
            resp.data != null &&
            resp.data.length > 0 &&
            resp.data[0] != null &&
            getGoodStanding(resp.data[0])
        );
    });
};

const getGoodStanding = (metricsStreamerStanding: StreamerStanding): boolean => {
    return (
        metricsStreamerStanding.donation_received_last_30_days >= MIN_STREAMER_STANDING_DONATION &&
        metricsStreamerStanding.total_session_ms_last_30_days >= MIN_STREAMER_STANDING_SESSIONS_MS
    );
};

export const getStreamerStanding = async (
    user_id: string,
    page: string,
    token: string,
): Promise<StreamerStandingResponse | null> => {
    const logPrefix = `[${LogPrefix}.getStreamerStanding]`;

    let query = '';
    if (page === '') {
        query = `user_id=${user_id}&end_ms=${Date.now()}`;
    } else {
        query = page;
    }

    const url = `${config.analyticsServiceURL}/metrics/streamer_standing?${query}`;
    const resp = await fetch(url, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'Get',
    });

    if (resp.status !== 200) {
        console.error(`${logPrefix} url: ${url} error: ${await resp.text()}`);
        return null;
    }

    return await resp.json();
};

export interface SessionsWithPlayerCountResponse {
    type: string;
    data: SessionDataWithPlayerCount[];
    page: PaginatedData;
}

export class SessionDataWithPlayerCount {
    session_id: string = '';
    license: string = '';
    owner: string = '';
    room_created_ms: number = 0;
    room_destroyed_ms: number = 0;
    actor_info_timestamp: number = 0;
    max_rmb_player_count: number = 0;
}

export const getSessionsWithPlayerCount = async (
    session_id: string,
    license: string,
    owner: string,
    player_count_low: number,
    player_count_high: number,
    sort_order: string,
    page: string,
    token: string,
): Promise<SessionsWithPlayerCountResponse | null> => {
    const logPrefix = `[${LogPrefix}.getSessionsWithPlayerCount]`;

    let query = '';
    if (page === '') {
        query = `session_id=${session_id}&license=${license}&owner=${owner}`;
        query += `&player_count_low=${player_count_low}&player_count_high=${player_count_high}`;
        query += `&sort_order=${sort_order}`;
    } else {
        query = page;
    }

    const url = `${config.analyticsServiceURL}/sessions/player_count?${query}`;
    const resp = await fetch(url, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'Get',
    });

    if (resp.status !== 200) {
        console.error(`${logPrefix} url: ${url} error: ${await resp.text()}`);
        return null;
    }

    return await resp.json();
};

export interface TransactionsResponse {
    type: string;
    data: TransactionRecord[];
    page: PaginatedData;
}

export interface TransactionRecord {
    event: string;

    // Unix timestamp in milliseconds of the transaction.
    timestamp_ms: number;

    // User of the inventory in which this transaction occurred.
    user_id: string;

    // array of product definition json
    products: ProductDef[];

    // array of inventory definition json
    inventory_items: InventoryDef[];

    // array of inventory defintion json that was removed due to this transaction
    inventory_items_removed: InventoryDef[];

    // array of subscription definition json
    subscription_items: SubscriptionDef[];

    // for inventory.purchase, inventory.notify_appstore, inventory.notify_playstore, inventory.notify_xsolla

    // AppleAppStore / GooglePlay / Xsolla
    store: string;

    // currency when purchasing real product
    cost_currency: string;

    // item used as cost when purchasing virtual product
    cost_item_id: string;

    // amount of cost when purchase
    cost_quantity: number;

    // transac id from purchase
    transac_id: string;

    // notification from store webhook
    notification_type: string;

    // additional notification type from AppleAppStore
    sub_type: string;

    appstore_subscription: AppStoreSubscription;
    playstore_subscription: PlayStoreSubscription;
    xsolla_subscription: XsollaWebhookSubscription;
    stripe_subscription: StripeSubscription;
    steam_subscription: SteamSubscription;

    // for inventory.transfer_in and inventory.transfer_out

    // user who sent items to user_id
    transfer_sender: string;

    // user who received items from sender
    transfer_receiver: string;

    // amount transferred
    transfer_quantity: string;

    // record id from transfer
    record_id: string;
}

export const getTransactions = async (
    user_id: string,
    product_name: string,
    item_name: string,
    sort_order: string,
    page: string,
    token: string,
): Promise<TransactionsResponse | null> => {
    const logPrefix = `[${LogPrefix}.getTransactions]`;

    let query = '';
    if (page === '') {
        query = `user_id=${user_id}&product_name=${product_name}&item_name=${item_name}`;
        query += `&sort_order=${sort_order}`;
    } else {
        query = page;
    }

    const url = `${config.analyticsServiceURL}/transactions?${query}`;
    const resp = await fetch(url, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'Get',
    });

    if (resp.status !== 200) {
        console.error(`${logPrefix} url: ${url} error: ${await resp.text()}`);
        return null;
    }

    return await resp.json();
};
